import React from 'react';
import PropTypes from 'prop-types';

/**
 * LazyMount mounts its children only when the wrapper enters the viewport.
 * - Uses IntersectionObserver when available, otherwise falls back to immediate mount.
 * - Optional rootMargin/threshold to preload slightly before visible.
 * - If `once` is true (default), stops observing after first mount.
 */
export default function LazyMount({
    children,
    root,
    rootMargin,
    threshold,
    once,
    fallbackDelay,
    placeholder,
    className,
    style
}) {
    const ref = React.useRef(null);
    const [visible, setVisible] = React.useState(false);

    React.useEffect(() => {
        let observer;
        let timeoutId;

        const mount = () => {
            if (!visible) setVisible(true);
        };

        // Optional fallback timer
        if (fallbackDelay > 0) {
            timeoutId = setTimeout(mount, fallbackDelay);
        }

        if ('IntersectionObserver' in window && ref.current) {
            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting || entry.intersectionRatio > 0) {
                            mount();
                            if (once && observer && ref.current) {
                                observer.unobserve(ref.current);
                            }
                        }
                    });
                },
                { root, rootMargin, threshold }
            );
            observer.observe(ref.current);
        } else {
            // No IO support – mount immediately
            mount();
        }

        return () => {
            if (observer && ref.current) observer.unobserve(ref.current);
            if (observer) observer.disconnect();
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [root, rootMargin, threshold, once, fallbackDelay, visible]);

    return (
        <div ref={ref} className={className} style={style}>
            {visible ? children : (placeholder || null)}
        </div>
    );
}

LazyMount.propTypes = {
    children: PropTypes.node,
    root: PropTypes.any,
    rootMargin: PropTypes.string,
    threshold: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.number)
    ]),
    once: PropTypes.bool,
    fallbackDelay: PropTypes.number,
    placeholder: PropTypes.node,
    className: PropTypes.string,
    style: PropTypes.object
};

LazyMount.defaultProps = {
    root: null,
    rootMargin: '200px',
    threshold: 0,
    once: true,
    fallbackDelay: 0,
    placeholder: null,
    className: undefined,
    style: undefined
};



// WEBPACK FOOTER //
// ./src/components/LazyMount/LazyMount.js